import React, { useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Card, CardContent, Typography, Box, Grid, Button, ButtonGroup, useTheme, TextField, CircularProgress } from "@mui/material";

export default function DashboardCharts({
  registrationTrend,
  purchaseTrend,
  testAttemptTrend,
  allRegistrationTrend,
  allPurchaseTrend,
  allTestAttemptTrend,
  todayRegistrationTrend,
  todayPurchaseTrend,
  todayTestAttemptTrend,
}) {
  const theme = useTheme();
  const [filter, setFilter] = useState("month"); // 'today' | 'week' | 'month' | 'all' | 'custom'
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [customData, setCustomData] = useState(null); // fetched from API
  const [customLoading, setCustomLoading] = useState(false);
  const [customApplied, setCustomApplied] = useState(false);

  // ── Helpers ───────────────────────────────────────────

  /** Format YYYY-MM-DD → "3 Jun" */
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", { day: "numeric", month: "short" });
  };

  /** Format YYYY-MM → "Jun 2024" */
  const formatMonth = (monthStr) => {
    if (!monthStr) return "";
    const [year, month] = monthStr.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };

  // ── Generate last 30 days date array ───────────────────────────────────
  const getLast30Days = () => {
    const dates = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      dates.push(d.toISOString().split("T")[0]);
    }
    return dates;
  };
  const dates30Days = getLast30Days();

  // ── Align daily DB data to a continuous date array ─────────────────────
  const alignDailyData = (trendData, dateArray) => {
    const trendMap = {};
    if (Array.isArray(trendData)) {
      trendData.forEach((item) => {
        const dStr = item.date ? String(item.date).split(" ")[0] : "";
        if (dStr) trendMap[dStr] = (trendMap[dStr] || 0) + Number(item.count || 0);
      });
    }
    return dateArray.map((d) => trendMap[d] || 0);
  };

  // ── Align all-time monthly DB data ─────────────────────────────────────
  const alignMonthlyData = (trendData) => {
    if (!Array.isArray(trendData) || trendData.length === 0) return { labels: [], values: [] };
    const labels = trendData.map((item) => formatMonth(item.month));
    const values = trendData.map((item) => Number(item.count || 0));
    return { labels, values };
  };

  // ── Align hourly data (0–23) ───────────────────────────────────────────
  const alignHourlyData = (hourlyTrend) => {
    const hourlyMap = {};
    if (Array.isArray(hourlyTrend)) {
      hourlyTrend.forEach((item) => {
        if (item.hour !== undefined)
          hourlyMap[Number(item.hour)] = (hourlyMap[Number(item.hour)] || 0) + Number(item.count || 0);
      });
    }
    return Array.from({ length: 24 }, (_, h) => hourlyMap[h] || 0);
  };

  // ── Hourly labels ───────────────────────────────────────────────────────
  const hourlyLabels = [
    "12 AM","1 AM","2 AM","3 AM","4 AM","5 AM",
    "6 AM","7 AM","8 AM","9 AM","10 AM","11 AM",
    "12 PM","1 PM","2 PM","3 PM","4 PM","5 PM",
    "6 PM","7 PM","8 PM","9 PM","10 PM","11 PM",
  ];

  // ── Process all datasets ────────────────────────────────────────────────
  // Month (last 30 days, daily)
  const monthRegData   = alignDailyData(registrationTrend, dates30Days);
  const monthPurData   = alignDailyData(purchaseTrend, dates30Days);
  const monthTstData   = alignDailyData(testAttemptTrend, dates30Days);
  const monthLabels    = dates30Days.map(formatDate);

  // Week (last 7 of the 30)
  const weekLabels  = monthLabels.slice(-7);
  const weekRegData = monthRegData.slice(-7);
  const weekPurData = monthPurData.slice(-7);
  const weekTstData = monthTstData.slice(-7);

  // All-time (monthly)
  const allReg = alignMonthlyData(allRegistrationTrend);
  const allPur = alignMonthlyData(allPurchaseTrend);
  const allTst = alignMonthlyData(allTestAttemptTrend);

  // Today (hourly)
  const todayRegData = alignHourlyData(todayRegistrationTrend);
  const todayPurData = alignHourlyData(todayPurchaseTrend);
  const todayTstData = alignHourlyData(todayTestAttemptTrend);

  // ── Fetch custom range data from API ────────────────────────────────────
  const handleApplyCustom = async () => {
    if (!customStart || !customEnd) return;
    setCustomLoading(true);
    try {
      const token = localStorage.getItem("token");
      const url = `${process.env.REACT_APP_LIVE_URL_NEW}/admin/get-admin-dashboard?start_date=${customStart}&end_date=${customEnd}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json?.data) {
        setCustomData(json.data);
        setCustomApplied(true);
      }
    } catch (e) {
      console.error("Custom range fetch error:", e);
    }
    setCustomLoading(false);
  };

  // ── Build date range for custom ─────────────────────────────────────────
  const buildCustomDateRange = (start, end) => {
    const dates = [];
    const cur = new Date(start + "T00:00:00");
    const last = new Date(end + "T00:00:00");
    while (cur <= last) {
      dates.push(cur.toISOString().split("T")[0]);
      cur.setDate(cur.getDate() + 1);
    }
    return dates;
  };

  // ── Resolve active chart data ───────────────────────────────────────────
  let activeRegData, activePurData, activeTstData, activeLabels;

  switch (filter) {
    case "today":
      activeRegData = todayRegData;
      activePurData = todayPurData;
      activeTstData = todayTstData;
      activeLabels  = hourlyLabels;
      break;
    case "week":
      activeRegData = weekRegData;
      activePurData = weekPurData;
      activeTstData = weekTstData;
      activeLabels  = weekLabels;
      break;
    case "all":
      activeRegData = allReg.values;
      activePurData = allPur.values;
      activeTstData = allTst.values;
      activeLabels  = allReg.labels;
      break;
    case "custom":
      if (customApplied && customData && customStart && customEnd) {
        const customDates = buildCustomDateRange(customStart, customEnd);
        activeRegData = alignDailyData(customData.custom_registration_trend, customDates);
        activePurData = alignDailyData(customData.custom_purchase_trend, customDates);
        activeTstData = alignDailyData(customData.custom_test_attempt_trend, customDates);
        activeLabels  = customDates.map(formatDate);
      } else {
        // Show empty until user applies
        activeRegData = [];
        activePurData = [];
        activeTstData = [];
        activeLabels  = [];
      }
      break;
    case "month":
    default:
      activeRegData = monthRegData;
      activePurData = monthPurData;
      activeTstData = monthTstData;
      activeLabels  = monthLabels;
      break;
  }

  const sumReg      = activeRegData.reduce((a, b) => a + b, 0);
  const sumPurchase = activePurData.reduce((a, b) => a + b, 0);
  const sumAttempts = activeTstData.reduce((a, b) => a + b, 0);

  // ── Chart options ───────────────────────────────────────────────────────
  const getChartOptions = (color, title, categoryLabels) => ({
    chart: {
      id: title.replace(/\s+/g, "-").toLowerCase(),
      toolbar: { show: false },
      zoom: { enabled: false },
      background: "transparent",
      fontFamily: theme.typography.fontFamily,
    },
    grid: {
      show: true,
      borderColor: "#e0e0e0",
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    stroke: { curve: "smooth", width: 3 },
    colors: [color],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.5,
        opacityTo: 0.05,
        stops: [0, 100],
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: categoryLabels,
      tickAmount: categoryLabels.length > 14 ? Math.min(10, Math.floor(categoryLabels.length / 3)) : undefined,
      labels: {
        show: true,
        style: { colors: "#637381", fontSize: "10px" },
        rotate: categoryLabels.length > 20 ? -35 : 0,
        rotateAlways: false,
        hideOverlappingLabels: true,
        trim: true,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        show: true,
        style: { colors: "#637381", fontSize: "10px" },
        formatter: (val) => Math.round(val),
      },
    },
    tooltip: {
      x: { show: true },
      y: { formatter: (val) => `${val}` },
    },
  });

  const handleFilterClick = (f) => {
    setFilter(f);
    if (f !== "custom") {
      setCustomApplied(false);
      setCustomData(null);
    }
  };

  return (
    <Box sx={{ mt: 5, mb: 2 }}>
      {/* Header row */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: "700", color: "#212b36" }}>
            Performance Trends
          </Typography>

          <ButtonGroup size="small" aria-label="date filters" color="primary">
            {["today", "week", "month", "all", "custom"].map((f) => (
              <Button
                key={f}
                variant={filter === f ? "contained" : "outlined"}
                onClick={() => handleFilterClick(f)}
                sx={{ textTransform: "capitalize" }}
              >
                {f === "custom" ? "Custom Range" : f.charAt(0).toUpperCase() + f.slice(1)}
              </Button>
            ))}
          </ButtonGroup>
        </Box>

        {/* Custom date range picker */}
        {filter === "custom" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              alignSelf: "flex-end",
              bgcolor: "background.neutral",
              p: 1.5,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              flexWrap: "wrap",
            }}
          >
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={customStart}
              onChange={(e) => { setCustomStart(e.target.value); setCustomApplied(false); }}
              InputLabelProps={{ shrink: true }}
            />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>to</Typography>
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={customEnd}
              onChange={(e) => { setCustomEnd(e.target.value); setCustomApplied(false); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: customStart || undefined }}
            />
            <Button
              variant="contained"
              size="small"
              disabled={!customStart || !customEnd || customLoading}
              onClick={handleApplyCustom}
              startIcon={customLoading ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {customLoading ? "Loading..." : "Apply"}
            </Button>
            {customApplied && (
              <Typography variant="caption" sx={{ color: "success.main" }}>
                ✓ Showing {customStart} → {customEnd}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Chart 1: Student Registrations */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2, boxShadow: "0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)", bgcolor: "background.paper" }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: "#637381", fontWeight: "600", textTransform: "uppercase" }}>
                  Student Registrations
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: "800", mt: 0.5, color: "#212b36" }}>
                  {sumReg.toLocaleString()}
                </Typography>
              </Box>
              <Box dir="ltr">
                <ReactApexChart
                  options={getChartOptions("#1890FF", "registrations", activeLabels)}
                  series={[{ name: "Registrations", data: activeRegData }]}
                  type="area"
                  height={280}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Chart 2: Course Purchases */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2, boxShadow: "0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)", bgcolor: "background.paper" }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: "#637381", fontWeight: "600", textTransform: "uppercase" }}>
                  Course Purchases
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: "800", mt: 0.5, color: "#212b36" }}>
                  {sumPurchase.toLocaleString()}
                </Typography>
              </Box>
              <Box dir="ltr">
                <ReactApexChart
                  options={getChartOptions("#54D62C", "purchases", activeLabels)}
                  series={[{ name: "Purchases", data: activePurData }]}
                  type="area"
                  height={280}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Chart 3: Tests Attempted */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2, boxShadow: "0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)", bgcolor: "background.paper" }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: "#637381", fontWeight: "600", textTransform: "uppercase" }}>
                  Tests Attempted
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: "800", mt: 0.5, color: "#212b36" }}>
                  {sumAttempts.toLocaleString()}
                </Typography>
              </Box>
              <Box dir="ltr">
                <ReactApexChart
                  options={getChartOptions("#FFC107", "attempts", activeLabels)}
                  series={[{ name: "Attempts", data: activeTstData }]}
                  type="area"
                  height={280}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
